'use client';

import 'BomdisoftComponents/drawers/styles.css';
import { Button } from 'BomdisoftComponents/buttons/index';
import { DEFAULT_EDIT_DRAWER_WIDTH, toEditDrawerSlot } from 'BomdisoftComponents/drawers/render';
import { useRegisterDrawer } from 'BomdisoftComponents/layout/hooks';
import { SidePanel } from 'BomdisoftComponents/side-panel/index';
import { type ReactNode, useEffect, useState } from 'react';

export { DEFAULT_EDIT_DRAWER_WIDTH } from 'BomdisoftComponents/drawers/render';

export const useRegisterEditDrawer = (drawerId: string, width = DEFAULT_EDIT_DRAWER_WIDTH) => {
  useRegisterDrawer(drawerId, toEditDrawerSlot(width));
};

export type EditDrawerProps = {
  children: ReactNode;
  /** Translated, for the tooltip and the accessible name both. Required, with
   *  no default: a default was how this shipped an English label into eighteen
   *  locales for as long as the drawer has existed. */
  closeLabel: string;
  testId?: string;
  onClose: () => void;
  onSave?: () => void;
  open: boolean;
  saveDisabled?: boolean;
  saveLabel?: string;
  title: string;
  /** A chip or badge that belongs beside the title rather than in the body: an
   * identifier the panel is about is part of naming it, not part of its
   * content. */
  titleAdornment?: ReactNode;
  width?: number;
};

/**
 * The drawer a grid row opens to edit what it names.
 *
 * **The card is `SidePanel`'s**, docked: the same contained box every panel
 * beside a grid draws, arriving in a column grown from nothing so the content
 * beside it moves over. What lives here is the Save.
 */
export const EditDrawer = ({ children, closeLabel, testId, onClose, onSave, open, saveDisabled, saveLabel = 'Save', title, titleAdornment, width = DEFAULT_EDIT_DRAWER_WIDTH }: EditDrawerProps) => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  if (!mounted) return null;

  return (
    <SidePanel
      closeLabel={closeLabel}
      footer={
        onSave === undefined ? undefined : (
          <Button data-testid={testId ? `${testId}-save` : undefined} disabled={saveDisabled} onClick={onSave} variant='contained'>
            {saveLabel}
          </Button>
        )
      }
      onClose={onClose}
      open={open}
      placement='docked'
      testId={testId}
      title={
        titleAdornment === undefined ? (
          title
        ) : (
          <span className='bomdisoft-edit-drawer-title-group'>
            {title}
            {titleAdornment}
          </span>
        )
      }
      width={width}
    >
      {children}
    </SidePanel>
  );
};
